import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { ProfitPanel, FunnelPanel, ChatStatsPanel } from "@/components/AdminInsights";
import { AdminBundleStats } from "@/components/AdminBundleStats";
import { AdminAppStats } from "@/components/AdminAppStats";

const Stat = ({ id, label, value }) => (
  <div data-testid={ADMIN.dashTotal(id)} className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5">
    <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-stone-500">{label}</p>
    <p className="mt-2 font-heading text-xl sm:text-3xl font-extrabold">{value}</p>
  </div>
);

const List = ({ title, items, empty, render }) => (
  <div className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5">
    <p className="font-heading font-bold">{title}</p>
    {items.length === 0 ? <p className="mt-3 text-sm text-stone-500">{empty}</p> : <ol className="mt-3 space-y-2 text-sm">{items.map(render)}</ol>}
  </div>
);

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const AdminDashboard = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState(null);
  const load = () => {
    setFailed(false);
    api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => setFailed(true));
  };
  useEffect(load, [refreshKey]);
  if (failed)
    return (
      <div className="mt-8 rounded-[1.5rem] bg-[#FDF2EC] p-6 text-sm">
        Couldn't load the sales dashboard.{" "}
        <button data-testid="admin-dash-retry" onClick={load} className="underline text-[#E06D44]">Try again</button>
      </div>
    );
  if (!d) return <div className="mt-8 h-40 rounded-[1.5rem] bg-[#F0EBE1] animate-pulse" />;
  const max = Math.max(...d.daily.map((x) => x.sales), 1);
  const sel = d.daily.find((x) => x.date === picked);
  return (
    <section data-testid={ADMIN.dashboard} className="mt-8 space-y-4">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Stat id="sales" label={`Sales, last ${d.days} days`} value={money(d.totals.sales)} />
        <Stat id="orders" label="Orders" value={d.totals.orders} />
        <Stat id="to-ship" label="Waiting to ship" value={d.totals.to_ship} />
      </div>
      <div className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5">
        <div className="flex flex-wrap justify-between gap-2">
          <p className="font-heading font-bold">Daily sales</p>
          <p data-testid="admin-dash-picked" className="text-sm text-stone-600">
            {sel ? `${new Date(`${sel.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}: ${money(sel.sales)} · ${plural(sel.orders, "order")}` : "Tap a bar to see that day"}
          </p>
        </div>
        <div className="mt-4 flex items-end gap-1.5 h-36">
          {d.daily.map((x) => (
            <button type="button" key={x.date} onClick={() => setPicked(x.date)} aria-label={`Sales on ${x.date}`} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
              <div
                data-testid={ADMIN.dashBar(x.date)}
                title={`${x.date}: ${money(x.sales)} · ${x.orders} orders`}
                className={`w-full rounded-t-md transition-[height,background-color] duration-500 ${picked === x.date ? "bg-[#1C1917]" : "bg-[#E06D44]"}`}
                style={{ height: `${Math.max((x.sales / max) * 100, x.sales ? 4 : 1)}%`, opacity: x.sales ? 1 : 0.2 }}
              />
              <span className="text-[10px] text-stone-400">{new Date(`${x.date}T12:00:00`).getDate()}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <List title={`Best sellers, last ${d.days} days`} items={d.best_sellers} empty="No sales yet." render={(b, i) => (
          <li key={b.slug} data-testid={ADMIN.bestSeller(b.slug)} className="flex justify-between gap-3"><span>{i + 1}. {b.name}</span><span className="text-stone-500 whitespace-nowrap">{b.qty} sold · {money(b.revenue)}</span></li>
        )} />
        <List title={`Top creators, last ${d.days} days`} items={d.top_creators} empty="No creator sales yet." render={(c, i) => (
          <li key={c.handle} data-testid={ADMIN.topCreator(c.handle)} className="flex justify-between gap-3"><span>{i + 1}. @{c.handle}</span><span className="text-stone-500 whitespace-nowrap">{plural(c.orders, "order")} · {money(c.sales)}</span></li>
        )} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ProfitPanel p={d.profit} />
        <FunnelPanel f={d.funnel} rows={d.product_insights} />
      </div>
      {d.chat && <ChatStatsPanel c={d.chat} />}
      <AdminBundleStats refreshKey={refreshKey} />
      <AdminAppStats refreshKey={refreshKey} />
    </section>
  );
};
